import { reduxStore } from "../stores/reduxStore";
import { Token, authSlice } from "../stores/slices/authSlice";
import { bugsnagNotify } from "../util/bugsnag";
import { HANDOFF_CODE_PATTERN } from "../util/handoffCode";
import { storeSSOToken, takePendingHandoffCode } from "../util/parseToken";
import { fetcher } from "./fetcher";
import { getApiBaseUrl } from "./getApiBase";

// One-time hand-off codes (TW-1087): wherever a URL has to carry the session (the CMS
// link, the PDF export, the CMS-to-site SSO fragment) it carries a code minted for the
// token instead of the token. A code is 48 hex characters, lives a minute, and is
// exchanged exactly once; the receiving side names the purpose it was minted for.
export type HandoffPurpose = "cms" | "pdf" | "site";

const createPath = "/retailSiteWebService/authentication/createHandoffCode.php";
const exchangePath =
  "/retailSiteWebService/authentication/exchangeHandoffCode.php";

type HandoffCode = { code: string; expiresAt: string; purpose: string };

/** Mints a code for the current token. Goes through the fetcher, so it carries the token header. */
export const createHandoffCode = async (
  purpose: HandoffPurpose
): Promise<string> => {
  const { payload } = await fetcher<HandoffCode>(createPath, {
    body: new URLSearchParams({ purpose }),
    method: "POST",
  });
  return payload.code;
};

/**
 * Turns a code back into its token, once. A plain fetch rather than the fetcher: it
 * runs before the app is up, needs no token, and a refusal is not an error toast.
 */
export const exchangeHandoffCode = async (
  code: string,
  purpose: HandoffPurpose
): Promise<Token | null> => {
  if (!HANDOFF_CODE_PATTERN.test(code)) {
    return null;
  }
  let apiURL = `${getApiBaseUrl()}${exchangePath}`;
  if (window.stripPhpExtension) {
    apiURL = apiURL.replace(".php", "");
  }
  const res = await fetch(apiURL, {
    body: new URLSearchParams({ code, purpose }),
    credentials: "same-origin",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    method: "POST",
    referrerPolicy: "strict-origin",
  });
  if (!res.ok) {
    return null;
  }
  const json = await res.json();
  const token = json?.token as Token | undefined;
  return json?.success && typeof token?.tokenId === "string" ? token : null;
};

/**
 * Boot step (Application.v2.tsx): a `#/sso/<code>` fragment, left by the CMS "login
 * to dealer retail site" link, was captured by parseToken while the auth slice was
 * created. Exchange it before the first render so the first requests already carry
 * the signed-in token; a refused code leaves the site anonymous.
 */
export const exchangePendingHandoffCode = async (): Promise<void> => {
  const code = takePendingHandoffCode();
  if (!code) {
    return;
  }
  const token = await exchangeHandoffCode(code, "site").catch(
    (error: Error) => {
      bugsnagNotify(error);
      return null;
    }
  );
  if (!token) {
    bugsnagNotify(new Error("The SSO hand-off code was refused"));
    return;
  }
  storeSSOToken(token);
  reduxStore.dispatch(authSlice.actions.replaceToken(token));
};
